import type { PaxtonHighlightArea } from '@/components/react-pdf-viewer/schema/paxton-highlight-area'
import type { ContentViewerSession } from '@/content-viewer/store/schemas'
import type { AppDispatch } from '@/store/store'
import { fetchAndStoreUserFiles } from '@/user_files/files/utils/fetch-and-store-user-files'
import type { MedChronReference } from '@/medical-chronology/references/store/schemas'
import { buildMedChronViewerSession } from './build-med-chron-viewer-session'
import { type MedChronSourceRef } from './schemas'

export type OpenMedChronSourceViewerOptions = {
  reference?: MedChronReference
  highlights?: PaxtonHighlightArea[]
  startingPage?: number
}

export type OpenMedChronSourceViewerResult =
  | {
      opened: true
      session: ContentViewerSession
    }
  | {
      opened: false
      error: string
    }

type OpenMedChronSourceViewerArgs = {
  source: MedChronSourceRef
  options?: OpenMedChronSourceViewerOptions
  dispatch: AppDispatch
}

export async function openMedChronSourceViewer(args: OpenMedChronSourceViewerArgs): Promise<OpenMedChronSourceViewerResult> {
  const { source, options = {}, dispatch } = args

  // Always re-fetch rather than trusting a possibly-stale cached record: a file loaded
  // before it was deleted would otherwise open a session for content that no longer exists.
  const records = await fetchAndStoreUserFiles([source.source_id], dispatch)
  const userFile = records[source.source_id]

  const session = buildMedChronViewerSession({
    source,
    reference: options.reference,
    userFile,
    highlights: options.highlights,
    startingPage: options.startingPage,
  })

  return session ? { opened: true, session } : { opened: false, error: 'Could not load this source file.' }
}
